import { useSetAtom } from "jotai";
import { ReactNode } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router";

import { Calendar, InfoCircle } from "@/assets/icons/ds";
import { Notification } from "@/components/Interface/Notification/Notification";
import { CardModule } from "@/components/Layout/BaseLayouts/CardModule/CardModule";
import {
  StatusDateLine,
  StatusSummaryBox,
  StatusSummaryBoxProps,
} from "@/components/Portal/DeviceManagement/components/StatusSummaryBox";
import { SubscriptionActionButton } from "@/components/Portal/DeviceManagement/SubscriptionDetails/SubscriptionDisplay/SubscriptionActionButton/SubscriptionActionButton";
import { portalRoutes } from "@/router/routes/portalRoutes";
import { SimCardDetail } from "@/services/model";
import {
  portalTariffProductIDAtom,
  tariffCheckoutPaymentPersistenceAtom,
} from "@/utils/atoms";
import { isLockRequested } from "@/utils/deviceUtils";
import { getElapsedPercentage, getNextBillDate } from "@/utils/tariffUtils";

type SubscriptionState = "locked" | "preparing" | "pausing" | "renewing";

const getSubscriptionState = (simDetail: SimCardDetail): SubscriptionState => {
  if (isLockRequested(simDetail)) {
    return "locked";
  }

  if (simDetail.servicePending) {
    return "preparing";
  }

  // Auto-renewal is off and nothing else is going on: the subscription is
  // scheduled to stop renewing on `nextBillDate`, i.e. it is "wird pausiert".
  // `autoRenew` is optional - undefined (not yet provisioned, or a tariff
  // type that never populates it) must not be mistaken for "off".
  return simDetail.autoRenew === false ? "pausing" : "renewing";
};

/**
 * "Subscription status": whether the subscription renews, when it is billed next, and
 * why it is blocked when a payment could not be collected.
 */
export const SubscriptionStatus = ({
  simDetail,
  action,
  billingPeriodDisabledHint,
  showBillingPeriod = true,
}: {
  simDetail: SimCardDetail;
  action?: ReactNode;
  /** Same disable rules as "Change" / "Pause subscription" — greyed out with tooltip. */
  billingPeriodDisabledHint?: string;
  /** Flat Home has a fixed billing period for the whole contract term. */
  showBillingPeriod?: boolean;
}) => {
  const { t, i18n } = useTranslation("portal", {
    keyPrefix: "cockpit.managementTile.subscription.status",
  });
  const { t: tDefault } = useTranslation();
  const navigate = useNavigate();
  const setPortalTariffProductID = useSetAtom(portalTariffProductIDAtom);
  const setTariffPaymentPersistence = useSetAtom(
    tariffCheckoutPaymentPersistenceAtom,
  );

  const handleBillingPeriodSwitch = () => {
    if (!simDetail.serviceID || !simDetail.simId) {
      return;
    }
    setTariffPaymentPersistence(undefined);
    setPortalTariffProductID(simDetail.serviceID);
    navigate(
      `${portalRoutes.manageDevice.subscription.checkout}?simId=${simDetail.simId}&billingPeriodSwitch=true`,
    );
  };

  const hasLockRequested = isLockRequested(simDetail);
  const status = getSubscriptionState(simDetail);

  const nextBillDate = getNextBillDate(simDetail, i18n.language);
  const elapsedPercentage = getElapsedPercentage(simDetail);
  const active = tDefault("portal:common.status.active");

  const summary: Record<SubscriptionState, StatusSummaryBoxProps> = {
    locked: { state: "locked", heading: t("locked") },
    preparing: {
      state: "renewing",
      heading: active,
      subline: t("inPreparation"),
      progress: elapsedPercentage,
    },
    pausing: {
      state: "pausing",
      heading: active,
      subline: nextBillDate && (
        <StatusDateLine
          i18nKey="cockpit.managementTile.subscription.status.pausing"
          date={nextBillDate}
        />
      ),
      progress: elapsedPercentage,
    },
    renewing: {
      state: "renewing",
      heading: active,
      subline: nextBillDate && (
        <StatusDateLine i18nKey="common.nextBill" date={nextBillDate} />
      ),
      progress: elapsedPercentage,
    },
  };

  // `minContractDuration` is occasionally missing from the API for an
  // otherwise-active subscription; the billing period section only needs to
  // know monthly vs. yearly, so default to monthly (30 days) rather than
  // hiding the whole section - same `?? 30` fallback used elsewhere
  // (SubscriptionPrice, SubscriptionAutoRenewal, AddWatchSimOption,
  // ChangeWatchSimRenewal) for the same field.
  const isMonthly = (simDetail.minContractDuration ?? 30) === 30;

  return (
    <CardModule
      dataTestid="subscription-status"
      title={t("title")}
      actions={action}
      bodyGap={2}
    >
      <StatusSummaryBox {...summary[status]} />

      {showBillingPeriod && (
        <div className="flex flex-col gap-2.5">
          <div className="flex items-center gap-3 rounded-md bg-surface-quaternary p-4">
            <Calendar className="size-6 shrink-0 text-foreground-primary" />
            <div className="flex min-w-0 flex-1 flex-col gap-1 leading-none text-foreground-primary">
              <p className="truncate-safe text-body-base font-semibold leading-none">
                {isMonthly
                  ? tDefault(
                      "Onboarding.sections.checkout.billingPeriod.monthly",
                    )
                  : tDefault(
                      "Onboarding.sections.checkout.billingPeriod.yearly",
                    )}
              </p>
              <p className="truncate-safe text-body-xs leading-none">
                {t("billingPeriod.label")}
              </p>
            </div>
            <SubscriptionActionButton
              dataTestid="subscription-billing-period-change"
              variant="secondary"
              label={t("billingPeriod.change")}
              disabled={!simDetail.serviceID || !simDetail.simId}
              disabledHint={billingPeriodDisabledHint}
              onClick={handleBillingPeriodSwitch}
            />
          </div>
          {isMonthly && (
            <div className="flex gap-2 text-body-sm leading-sm text-foreground-primary">
              <span className="inline-flex h-[1.2em] shrink-0 items-center">
                <InfoCircle className="size-4" aria-hidden />
              </span>
              <p className="min-w-0 flex-1">
                <Trans
                  t={tDefault}
                  i18nKey="Onboarding.sections.checkout.billingPeriod.description"
                  components={[<span key="0" className="font-semibold" />]}
                />
              </p>
            </div>
          )}
        </div>
      )}

      {hasLockRequested && (
        <Notification
          dataTestid="subscription-payment-failed"
          tone="error"
          title={t("paymentFailed.title")}
          description={
            <Trans
              t={t}
              i18nKey="paymentFailed.text"
              components={[
                <Link
                  key="0"
                  className="underline"
                  replace
                  to={portalRoutes.documents.payments}
                />,
              ]}
            />
          }
        />
      )}
    </CardModule>
  );
};
